// AXIOM — adaptive quality governor
//
// The engine used to boot at `high` on every machine regardless of what that
// machine could actually do. That is fine on the Metal hardware this was built
// on and reportedly very heavy on Windows Chrome, where WebGL runs through
// ANGLE onto D3D11: per-draw-call CPU cost is several times higher, every
// shader is re-translated GLSL -> HLSL -> D3D bytecode, and ten full-screen post
// passes at a supersampled resolution become fill-rate bound immediately.
//
// Nobody should have to find the settings menu to get a playable frame rate, and
// we cannot detect "is this GPU fast" reliably from a string. So measure it:
// watch real frame times and step the preset down until the game holds its
// budget. Stepping UP is deliberately much more reluctant than stepping down —
// oscillating between presets is worse than sitting one notch low.
//
// -------------------------------------------------------------------------
// WHAT IT USED TO MEASURE, AND WHY THAT WAS WRONG
// -------------------------------------------------------------------------
// The first version timed the interval between its own invocations and demoted
// whenever the median exceeded a hard-coded 15 ms. But the loop is driven by
// requestAnimationFrame, so that interval is the DISPLAY PERIOD, not the cost of
// the frame. On a 60 Hz panel a machine with 3 ms of work still reports 16.7 ms
// per frame — above the 15 ms budget — so it demoted every 90 frames until it
// bottomed out at `low`, and could never come back: promotion required a median
// under 8 ms, which 60 Hz vsync makes physically unreachable. Every 60 Hz player
// was silently forced to `low` within about three seconds of starting a run, on
// any hardware. On a 120 Hz panel the same thresholds pin it at `high` forever
// and it never reacts at all.
//
// So there are two signals now, and neither is an absolute millisecond count:
//
//   work     — time actually spent in the frame (update + render + submit),
//              sampled by Game._frame. This is the number that says "we cannot
//              possibly go faster than this", and it is refresh-rate agnostic.
//   interval — wall-clock between frames. Compared against the *observed*
//              display period rather than a constant: the fastest frames in a
//              window are, by definition, the ones with no backlog, so their
//              duration is the refresh period. A median well above that period
//              means we are missing vsyncs.
//
// A preset is too expensive if either signal says so. It is cheap enough to
// raise only if both agree, with margin.

import S from './Registry.js';
import { bus } from './EventBus.js';

const ORDER = ['low', 'medium', 'high', 'ultra'];

export const GOVERNOR = {
  window: 90,           // frames per judgement
  demoteAfter: 1,       // consecutive bad windows before dropping (react fast)
  promoteAfter: 8,      // consecutive good windows before raising (be reluctant)
  warmupFrames: 120,    // ignore shader-compile and first-wave hitches

  // All budgets are multiples of the measured display period.
  missRatio: 1.35,      // median interval this far over the period = dropping frames
  workRatio: 0.92,      // work alone eating this much of the period = no headroom
  promoteInterval: 1.08,// must be essentially locked to the refresh to promote
  promoteWork: 0.55,    // ...and the frame's own cost must be under half a period

  panicRatio: 2.2,      // a frame this far over the period is a visible hitch
  panicFloorMs: 33.0,   // ...but never panic on anything under ~30 fps worth of time
  panicCount: 2,        // ...and never on ONE of them: see _panics
  outlierMs: 120.0,     // above this it is a tab switch / GC / breakpoint, not GPU load
  refreshClamp: [5.5, 40.0],

  // Resolution ladder, walked before any preset is touched. See _step().
  scaleSteps: [1.0, 0.88, 0.78, 0.68, 0.58],
};

export class PerfGovernor {
  constructor() {
    this.enabled = true;

    /**
     * Whether the governor may CHANGE anything on its own. Off.
     *
     * It used to silently rewrite the player's graphics settings mid-run, and
     * that is the wrong behaviour regardless of how good the heuristic is. The
     * player chose a preset; a game that quietly overrules that choice — and
     * then blurs the image to a third of its resolution to do it — has taken
     * away the one control it offered them. "Why do my settings keep changing
     * on their own" is the correct reaction, not a misunderstanding.
     *
     * It also has a track record that does not earn the authority: it once
     * pinned every 60 Hz machine at `low` within three seconds by measuring
     * vsync instead of work, and on a 4K display it spent five demotions and
     * five full render-target reallocations without changing a single pixel.
     *
     * So it now measures and ADVISES. When a run is genuinely over budget it
     * says so once, and the player decides. Set `autoApply = true` (or expose a
     * settings toggle) to give it back the trigger.
     */
    this.autoApply = false;
    this.suggested = false;    // one advisory per run, not a nag
    this.hitches = [];         // frames excluded from judgement but not from QA
    this.worstMs = 0;
    this.raw = [];             // raw rAF intervals, unfiltered, for gate maths

    this.userLocked = false;   // set when the player picks a preset by hand
    this.samples = [];         // frame intervals (wall clock)
    this.work = [];            // frame work time, fed by Game._frame
    this.frame = 0;
    this.bad = 0;
    this.good = 0;
    this.changes = [];
    this.refreshMs = 16.7;     // running estimate of the display period
    this.lastJudgement = null;
    this.compileSkips = 0;
    this._panics = 0;
    this._last = performance.now();
    this._workMs = 0;
    this._programs = -1;
    bus.on('ui:qualityChosen', (p) => {
      this.userLocked = true;
      this.ceiling = p;
      // A hand-picked preset starts from a clean slate: the player asked for
      // this preset, not for this preset at 68% resolution because of whatever
      // the governor was reacting to five minutes ago.
      S.engine?.setRenderScale?.(1);
    });
  }

  /** Highest preset the governor may auto-select. */
  get ceiling() { return this._ceiling || 'high'; }
  set ceiling(v) { if (ORDER.includes(v)) this._ceiling = v; }

  /**
   * Cost of one frame's actual work, in ms. Called by Game._frame with the span
   * from the top of the update loop to the end of engine.render() — i.e. the
   * part we control, with the vsync wait excluded.
   */
  sample(ms) {
    if (Number.isFinite(ms) && ms >= 0 && ms < GOVERNOR.outlierMs) this._workMs = ms;
  }

  update() {
    if (!this.enabled || !S.engine?.setQuality) return;
    const now = performance.now();
    const ms = now - this._last;
    this._last = now;

    // Unfiltered ring of rAF intervals. Every gate figure a product audit needs
    // — p95, p99, 1% low, worst frame — has to come from the raw series, before
    // any of the exclusions below. Keeping it here is what lets an external
    // auditor compute those numbers without being able to drive the loop.
    if (ms > 0 && ms < 10000) {
      this.raw.push(ms);
      if (this.raw.length > 4096) this.raw.shift();
    }

    // Only judge frames that actually represent gameplay rendering cost.
    // A backgrounded tab, a paused menu, or a frame we did not drive ourselves
    // produces timings that have nothing to do with what the GPU can sustain —
    // and a spurious demotion is sticky, because promotion is deliberately slow.
    if (typeof document !== 'undefined' && document.hidden) return;
    if (S.state && S.state.phase !== 'playing') {
      // Boot, menus and the death screen are not gameplay, and their frames —
      // shader compilation, the splash, a paused world — would otherwise sit in
      // the raw series and poison every percentile an auditor computes from it.
      // Start the series fresh each time a run actually begins.
      this._wasPlaying = false;
      return;
    }
    if (!this._wasPlaying) {
      this._wasPlaying = true;
      this.raw.length = 0;
      this.hitches.length = 0;
      this.worstMs = 0;
      this.suggested = false;
    }
    if (ms > GOVERNOR.outlierMs) {
      // Excluded from the JUDGEMENT — a tab switch or a GC pause says nothing
      // about sustained GPU load — but recorded, because these are exactly the
      // frames a product gate cares about. Dropping them on the floor meant the
      // governor's own report could show a healthy median while the player was
      // watching the game freeze, and made this class of stall invisible to QA.
      const hl = this.hitches;
      hl.push({ ms: +ms.toFixed(1), frame: this.frame, at: +(now / 1000).toFixed(2) });
      if (hl.length > 200) hl.shift();
      this.worstMs = Math.max(this.worstMs, ms);
      return;
    }
    if (ms > this.worstMs) this.worstMs = ms;

    // A frame that compiled a shader is not evidence about this machine's
    // sustained speed — it is a one-off, and it is enormous: linking a patched
    // MeshStandardMaterial costs 30-150 ms, far past the panic threshold. The
    // first body of a new archetype, the first tracer, the first decal each pay
    // it once. Judging on those frames demoted a perfectly healthy machine two
    // presets inside the first wave, and promotion back takes eight good windows.
    const nPrograms = S.renderer?.info?.programs?.length ?? -1;
    if (nPrograms >= 0 && nPrograms !== this._programs) {
      const first = this._programs < 0;
      this._programs = nPrograms;
      if (!first) { this.compileSkips++; return; }
    }

    this.frame++;
    if (this.frame < GOVERNOR.warmupFrames) {
      this.samples.length = 0;
      this.work.length = 0;
      return;
    }
    this.samples.push(ms);
    this.work.push(this._workMs);

    // Catastrophic frames: 2.2x the refresh period, whatever that period is.
    // It takes TWO inside one window to cost a preset. One is not evidence —
    // the first ragdoll builds a cannon-es world, the first decal allocates an
    // atlas, the between-wave prewarm builds bodies, and any of those lands an
    // isolated 60-100 ms frame on hardware with enormous headroom. Demoting on
    // one of those is a mistake that takes eight good windows to undo, and it
    // reliably happened inside the first wave. Sustained overload, by contrast,
    // produces panic frames continuously, so this still reacts within ~2 frames.
    const panicMs = Math.max(GOVERNOR.panicFloorMs, this.refreshMs * GOVERNOR.panicRatio);
    if (ms > panicMs && this.frame > GOVERNOR.warmupFrames + 30) {
      if (++this._panics >= GOVERNOR.panicCount) {
        this._panics = 0;
        if (this._act(-1, `${GOVERNOR.panicCount} panic frames, last ${ms.toFixed(0)}ms (refresh ${this.refreshMs.toFixed(1)}ms)`)) {
          this.samples.length = 0; this.work.length = 0; return;
        }
      }
    }

    if (this.samples.length < GOVERNOR.window) return;

    const sorted = this.samples.slice().sort((a, b) => a - b);
    const workSorted = this.work.slice().sort((a, b) => a - b);
    this.samples.length = 0;
    this.work.length = 0;

    // The display period, measured rather than assumed. The fastest decile of a
    // vsync-locked window is the set of frames that had nothing queued ahead of
    // them, so their interval IS the refresh period. Taken as a slow-moving
    // average so one clean window cannot swing the budget.
    const p10 = sorted[Math.floor(sorted.length * 0.1)];
    const [lo, hi] = GOVERNOR.refreshClamp;
    const observed = Math.min(hi, Math.max(lo, p10));
    this.refreshMs += (observed - this.refreshMs) * 0.35;

    const median = sorted[sorted.length >> 1];
    const p95 = sorted[Math.floor(sorted.length * 0.95)];
    const workMed = workSorted[workSorted.length >> 1];
    const workP95 = workSorted[Math.floor(workSorted.length * 0.95)];
    const R = this.refreshMs;
    this._panics = 0;             // panics only count against their own window

    this.lastJudgement = {
      refreshMs: +R.toFixed(2), median: +median.toFixed(2), p95: +p95.toFixed(2),
      workMed: +workMed.toFixed(2), workP95: +workP95.toFixed(2),
    };

    // Too expensive if we are visibly missing vsyncs, OR if the frame's own work
    // already fills the period (the display is just hiding it for now).
    const missing = median > R * GOVERNOR.missRatio;
    const saturated = workMed > R * GOVERNOR.workRatio;

    if (missing || saturated) {
      this.good = 0;
      if (++this.bad >= GOVERNOR.demoteAfter) {
        this.bad = 0;
        this._act(-1, missing
          ? `median ${median.toFixed(1)}ms vs ${R.toFixed(1)}ms refresh (p95 ${p95.toFixed(1)})`
          : `work ${workMed.toFixed(1)}ms of a ${R.toFixed(1)}ms budget`);
      }
    } else if (median < R * GOVERNOR.promoteInterval && workP95 < R * GOVERNOR.promoteWork) {
      this.bad = 0;
      if (++this.good >= GOVERNOR.promoteAfter) {
        this.good = 0;
        this._act(+1, `work p95 ${workP95.toFixed(1)}ms of ${R.toFixed(1)}ms — headroom`);
      }
    } else {
      this.bad = 0;
      this.good = 0;
    }
  }

  /**
   * One notch of correction, cheapest lever first.
   *
   * The ladder is: resolution down through GOVERNOR.scaleSteps, and only when
   * that bottoms out, one preset down (which also resets the resolution, since
   * the cheaper preset should get its own full-resolution budget before we start
   * shaving it again). Going up reverses it exactly.
   *
   * The ordering is not a style choice. A preset swap rebuilds the CSM rig and
   * every post pass — measured at 872-2427 ms — while a rescale is 21-53 ms. The
   * governor only ever runs on a machine that is already missing frames, so
   * spending two seconds to save four milliseconds a frame is a bad trade the
   * player experiences as a freeze. Resolution is also the strongest single
   * lever when the bottleneck is fill, which is the ANGLE/D3D11 case this whole
   * subsystem exists for.
   */
  /**
   * Gate between "we decided a change is warranted" and actually making one.
   * With `autoApply` off this is where the decision stops and becomes advice.
   */
  _act(dir, reason) {
    if (this.autoApply) return this._step(dir, reason);
    // Only ever advise DOWNWARD, and only once. Nobody needs to be told their
    // machine has spare headroom, and nobody needs to be told twice.
    if (dir < 0 && !this.suggested) {
      this.suggested = true;
      this.lastAdvice = reason;
      console.info(`[perf] over budget — advising, not changing (${reason})`);
      bus.emit('perf:advice', { reason, quality: S.engine?.quality, auto: false });
      S.hud?.toast?.('描画が重いようです。設定から品質を下げられます');
    }
    return false;
  }

  _step(dir, reason) {
    const eng = S.engine;
    const steps = GOVERNOR.scaleSteps;

    // Presets first. Resolution is the LAST resort, not the first.
    //
    // This used to be the other way round, justified by the cost of a preset
    // change: it was measured at 872-2427 ms, so spending it on a machine that
    // is already struggling was worse than the stall it was trying to cure.
    // That reasoning is dead — the Lighting rebuild was the cost, and switches
    // inside the governor's range now take an in-place path at 2.1-4.3 ms.
    //
    // With the cost gone, the ordering should follow what it does to the image,
    // and there resolution is the worst lever available: it blurs the ENTIRE
    // frame, uniformly, including the HUD-adjacent detail the player is aiming
    // with. A preset step drops GTAO, motion blur and DOF — real GPU work, and
    // effects most players cannot name, let alone miss.
    //
    // It is also frequently the wrong lever on the merits. If dropping
    // resolution does not raise the frame rate, the bottleneck was never fill,
    // and the game has traded away its image for nothing — which is exactly the
    // "still heavy, but now blurry" state this ordering produced.
    if (dir < 0) {
      if (this._presetStep(dir, reason)) return true;
      return this._scaleStep(dir, reason);        // already at `low`
    }
    // Recovering: give the resolution back before reaching for a richer preset.
    if (this._scaleStep(dir, reason)) return true;
    return this._presetStep(dir, reason);
  }

  _scaleStep(dir, reason) {
    const eng = S.engine;
    if (!eng.setRenderScale) return false;
    const steps = GOVERNOR.scaleSteps;
    const i = this._scaleIndex();
    const next = i + (dir < 0 ? 1 : -1);
    if (next < 0 || next >= steps.length) return false;
    if (!eng.setRenderScale(steps[next])) return false;
    const cur = eng.quality || 'high';
    this.changes.push({ frame: this.frame, from: `${cur}@${steps[i]}`, to: `${cur}@${steps[next]}`, reason });
    console.info(`[perf] render scale ${steps[i]} -> ${steps[next]} (${reason})`);
    bus.emit('perf:scale', { from: steps[i], to: steps[next], reason, auto: true });
    this.frame = Math.min(this.frame, GOVERNOR.warmupFrames - 1);
    return true;
  }

  _scaleIndex() {
    const steps = GOVERNOR.scaleSteps;
    const s = S.engine.renderScale != null ? S.engine.renderScale : 1;
    let best = 0, bestD = Infinity;
    for (let i = 0; i < steps.length; i++) {
      const d = Math.abs(steps[i] - s);
      if (d < bestD) { bestD = d; best = i; }
    }
    return best;
  }

  _presetStep(dir, reason) {
    const cur = S.engine.quality || 'high';
    let i = ORDER.indexOf(cur);
    if (i < 0) i = 2;
    let next = i + dir;
    // Never auto-promote above the player's own choice, or above 'high'.
    const capIdx = ORDER.indexOf(this.userLocked ? this.ceiling : 'high');
    if (dir > 0 && next > capIdx) return false;
    if (next < 0 || next >= ORDER.length) return false;
    const preset = ORDER[next];
    if (preset === cur) return false;

    try { S.engine.setQuality(preset); }
    catch (e) { console.warn('[perf] setQuality failed', e); return false; }

    this.changes.push({ frame: this.frame, from: cur, to: preset, reason });
    console.info(`[perf] quality ${cur} -> ${preset} (${reason})`);
    bus.emit('perf:quality', { from: cur, to: preset, reason, auto: true });
    if (dir < 0) {
      S.hud?.toast?.(`描画品質を ${preset.toUpperCase()} に自動調整しました`);
    }
    // A preset switch reallocates render targets and recompiles programs; the
    // next few frames are not evidence of anything.
    this.frame = Math.min(this.frame, GOVERNOR.warmupFrames - 1);
    return true;
  }

  report() {
    return {
      quality: S.engine?.quality, renderScale: S.engine?.renderScale,
      frame: this.frame, changes: this.changes,
      refreshMs: +this.refreshMs.toFixed(2), last: this.lastJudgement,
      compileSkips: this.compileSkips,
    };
  }
}

export default PerfGovernor;
